import React, { useState, useEffect, useCallback } from 'react';
import { 
  Modal, 
  Box, 
  Typography, 
  Button, 
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import debounce from 'lodash/debounce';
import { fget } from '../../axios/axiosCalls';
import AddIcon from '@mui/icons-material/Add';
import AddCustomerForm from '../AddCustomerForm';
import { Search } from '@mui/icons-material';

const CustomerListModal = ({ open, handleClose, selectedCustomer, setSelectedCustomer }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null)
  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setShowConfirmation(true);
  };
  const columns = [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'phone', headerName: 'Phone', width: 150 },
  ];

  const fetchCustomers = useCallback(
    debounce(async (page, pageSize, search) => {
      setLoading(true);
      try {
        const response = await fget({ 
          url: '/api/v2/channelpartner/students',
          params: {
            page: page + 1,
            limit: pageSize,
            term: search
          }
        });
        if (response.data.status === 'Success') {
          setCustomers(response.data.students);
          setTotalRows(response.data?.pagination?.totalCount);
        }
      } catch (error) {
        console.error('Error fetching customers:', error);
      } finally {
        setLoading(false);
      }
    }, 500),
    []
  );
  const CustomToolbar = () => {
    const [searchInput, setSearchInput] = useState(searchTerm);
    
    useEffect(() => {
      setSearchInput(searchTerm);
    }, [searchTerm]);
  
    const handleSearch = () => {
      setSearchTerm(searchInput);
    };
  
    const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    };
  
    const handleClear = () => {
      setSearchInput('');
      setSearchTerm('');
    };
  
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <TextField
          size="small"
          placeholder="Search customers..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyPress={handleKeyPress}
          sx={{ width: 300 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <IconButton onClick={handleSearch} edge="end">
                    <Search />
                  </IconButton>
                )}
              </InputAdornment>
            )
          }}
        />
        <GridToolbar 
          sx={{
            '& .MuiButton-root': { p: '4px 8px' }
          }}
        />
      </Box>
    );
  };
  useEffect(() => {
    if (open) {
      fetchCustomers(
        paginationModel.page,
        paginationModel.pageSize,
        searchTerm
      );
    }
  }, [open, paginationModel, searchTerm]);
  const handleAddCustomerSuccess = (student) => {
    setShowAddForm(false);
    setSelectedCustomer(student)
    handleClose()
    // fetchCustomers(paginationModel.page, paginationModel.pageSize, searchTerm);
  };
  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '60%',
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        borderRadius: 1,
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        {!showAddForm ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Customer List</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowAddForm(true)}
              >
                Add Customer
              </Button>
            </Box>

        <DataGrid
          rows={customers}
          columns={columns}
          getRowId={row => row?.studentId}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          rowCount={totalRows}
          loading={loading}
          paginationMode="server"
          filterMode="server"
          onRowClick={(params) => setSelectedRow(params.row)}
          autoHeight
          sx={{ minHeight: 400 }}
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          slots={{
            toolbar: CustomToolbar,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: false,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
        />
        <Box sx={{ 
          mt: 3, 
          pt: 2,
          // borderTop: '1px solid',
          // borderColor: 'divider',
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: 2 
        }}>
          <Button onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            variant="contained"
            disabled={!selectedRow} 
            onClick={() =>{ setSelectedCustomer(selectedRow);handleClose()}}
          >
            Confirm Selection
          </Button>
        </Box>
  </>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Add New Student</Typography>
              <Button onClick={() => setShowAddForm(false)}>
                Back to List
              </Button>
            </Box>
            <AddCustomerForm onSuccess={handleAddCustomerSuccess} />
          </>
        )}
      </Box>
    </Modal>
  );
};

export default CustomerListModal;